import React, { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import {
  TrendingUp, History, CalendarDays, Wallet, Cloud, ChevronRight,
  Inbox, CheckCircle2, Lock, Trophy, Layers, Gift, ExternalLink, Sparkles, Clock, RefreshCw,
  Shield, HelpCircle,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiGet, apiPost, safeJson } from "@/lib/api";
import { LoadingBlock } from "@/components/Spinner";
import Modal from "@/components/Modal";
import ConfirmDialog from "@/components/ConfirmDialog";
import CalendarView from "@/screens/CalendarView";
import { CLOUDBET_REFERRAL_URL } from "@/constants/links";

const Stat = ({ label, value, color = "#e2e8f0" }) => (
  <div className="flex-1 text-center"><p className="text-base font-bold" style={{ color }}>{value}</p><p className="text-[10px] text-[#6b7280] mt-0.5">{label}</p></div>
);

const cleanStrategy = (s) => (s || "").replace(/\s*Strategy\s*$/i, "").trim();

// Settled-bet status color: WIN green, LOSS red, PUSH/VOID neutral gray.
const isLossStatus = (s) => /^(loss|lose|lost)$/i.test(s || "");
const isWinStatus = (s) => /^win$/i.test(s || "");
const statusColor = (s) => isWinStatus(s) ? "#10b981" : isLossStatus(s) ? "#ef4444" : "#94a3b8";

const CashedOutLine = ({ pos, testId }) => {
  if (!pos?.cashed_out) return null;
  const profit = typeof pos.cashout_locked_profit === "number" ? pos.cashout_locked_profit : null;
  const who = pos.cashout_hedge_player;
  return (
    <div
      data-testid={testId}
      className="flex items-center gap-1.5 mt-2 px-2 py-1.5 rounded-lg bg-[#06b6d4]/10 border border-[#06b6d4]/30"
    >
      <Shield size={12} className="text-[#22d3ee] shrink-0" />
      <span className="text-[11px] text-[#67e8f9] font-medium">
        Cashed out{who ? ` — hedged on ${who}` : ""}
        {profit !== null && (
          <span className="font-bold text-white"> · profit locked: {profit >= 0 ? "+" : ""}{profit.toFixed(2)} USDT</span>
        )}
      </span>
    </div>
  );
};

const StrategyTag = ({ strategy, testId }) => {
  const name = cleanStrategy(strategy);
  // Cash-out hedge bets: distinct cyan "Cash-Out" badge so the user knows this bet was placed by the
  // auto-cashout logic (a hedge on the opposite side), not a strategy signal. Also surface WHICH
  // trigger fired — Profit % vs Opponent Odds — when that info is recorded on the strategy label.
  if (/cash.?out/i.test(strategy || "")) {
    let trigger = "";
    if (/profit/i.test(strategy || "")) trigger = " · Profit %";
    else if (/opp/i.test(strategy || "")) trigger = " · Opp Odds";
    return (
      <span
        data-testid={testId}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#06b6d4]/15 border border-[#06b6d4]/40 text-[#22d3ee]"
      >
        <Shield size={10} /> Cash-Out{trigger}
      </span>
    );
  }
  // Unknown attribution (manual bet or legacy bet with no recorded strategy): show a neutral badge
  // so EVERY bet carries a label and the user is never left guessing.
  if (!name || /^auto$/i.test(name)) {
    return (
      <span
        data-testid={testId}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#64748b]/15 border border-[#64748b]/40 text-[#94a3b8]"
      >
        <HelpCircle size={10} /> Manual / Unknown
      </span>
    );
  }
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#8b5cf6]/15 border border-[#8b5cf6]/40 text-[#a78bfa]"
    >
      <Sparkles size={10} /> {name}
    </span>
  );
};

function parseMarket(marketUrl, eventName, backedPlayer, legs, backedPlayers) {
  const resolved = backedPlayer || (backedPlayers && backedPlayers.length > 0 ? backedPlayers.join(" + ") : undefined);
  if (legs && legs > 1 && resolved) return { label: `MULTI (${legs}-Leg)`, color: "#f59e0b", selection: resolved, Icon: Layers };
  if (resolved) {
    if (marketUrl?.includes("exact_sets")) { const m = marketUrl.match(/outcome=(\d+)/); return { label: "Exact Sets", color: "#8b5cf6", selection: `Yes (${m ? m[1] : "?"} Sets)`, Icon: Trophy }; }
    return { label: "Backing", color: "#10b981", selection: resolved, Icon: Trophy };
  }
  const parts = (eventName || "").split(/ vs | v /i);
  const home = parts[0]?.trim() || "Home"; const away = parts[1]?.trim() || "Away";
  if (marketUrl?.includes("exact_sets")) { const m = marketUrl.match(/outcome=(\d+)/); return { label: "Exact Sets", color: "#8b5cf6", selection: `Yes (${m ? m[1] : "?"} Sets)`, Icon: Trophy }; }
  if (marketUrl?.includes("winner/away")) return { label: "Backing", color: "#10b981", selection: away, Icon: Trophy };
  if (marketUrl?.includes("winner")) return { label: "Backing", color: "#10b981", selection: home, Icon: Trophy };
  return { label: "Other", color: "#6b7280", selection: marketUrl || "Unknown", Icon: Trophy };
}

const fmtTime = (iso) => { if (!iso) return ""; try { const d = new Date(iso); return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) + " " + d.toLocaleDateString([], { month: "short", day: "numeric" }); } catch { return iso; } };

// Module-level cache (survives tab unmount/remount) → instant render on return,
// with a silent background refresh. Spinner shows only on the very first load.
const _tCache = { open: [], settled: [], pnl: null, balance: 0, est: {}, history: [], cbKey: null, ready: false };

export default function Trading({ onNavigate }) {
  const { isAdmin } = useAuth();
  const [subTab, setSubTab] = useState("positions");
  const [openPositions, setOpenPositions] = useState(() => _tCache.open);
  const [settledBets, setSettledBets] = useState(() => _tCache.settled);
  const [pnl, setPnl] = useState(() => _tCache.pnl);
  const [balance, setBalance] = useState(() => _tCache.balance);
  const [cashoutEstimates, setCashoutEstimates] = useState(() => _tCache.est);
  const [history, setHistory] = useState(() => _tCache.history);
  const [historyRefreshing, setHistoryRefreshing] = useState(false);
  const [positionsRefreshing, setPositionsRefreshing] = useState(false);
  const [loading, setLoading] = useState(() => !_tCache.ready);
  const [hasCloudbetKey, setHasCloudbetKey] = useState(() => _tCache.cbKey);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [cashoutTarget, setCashoutTarget] = useState(null);
  const [cashoutLoading, setCashoutLoading] = useState(false);

  // Total stake at risk across open positions (memoized so it isn't recomputed every render).
  const atRiskTotal = useMemo(
    () => openPositions.reduce((a, p) => a + (p.stake || 0), 0),
    [openPositions]
  );

  // Split positions into PENDING (submitted, awaiting Cloudbet acceptance) and ACTIVE (accepted/open).
  const pendingPositions = useMemo(
    () => openPositions.filter((p) => (p.status || "").toUpperCase() === "PENDING"),
    [openPositions]
  );
  const activePositions = useMemo(
    () => openPositions.filter((p) => (p.status || "").toUpperCase() !== "PENDING"),
    [openPositions]
  );
  // Show settled bets newest-first (by settle time, fallback placed time) so the LATEST settled
  // bets appear at the top — matching the History tab (which sorts the same way server-side).
  const sortedSettledBets = useMemo(
    () => [...settledBets].sort((a, b) =>
      String(b.settled_at || b.placed_at || "").localeCompare(String(a.settled_at || a.placed_at || ""))
    ),
    [settledBets]
  );

  // Per-strategy P&L leaderboard, computed from settled bets (win-rate + net profit).
  const strategyBreakdown = useMemo(() => {
    const map = {};
    for (const b of settledBets) {
      const key = cleanStrategy(b.strategy) || "Unattributed";
      if (!map[key]) map[key] = { name: key, wins: 0, losses: 0, net: 0, staked: 0, count: 0 };
      const m = map[key];
      m.count += 1;
      m.staked += b.stake || 0;
      m.net += b.profit || 0;
      if (isWinStatus(b.status)) m.wins += 1; else if (isLossStatus(b.status)) m.losses += 1;
    }
    return Object.values(map)
      .map((m) => ({ ...m, winRate: m.count ? (m.wins / m.count) * 100 : 0, roi: m.staked ? (m.net / m.staked) * 100 : 0 }))
      .sort((a, b) => b.net - a.net);
  }, [settledBets]);

  const fetchPositions = useCallback(async (force) => {
    if (!_tCache.ready) setLoading(true);  // spinner only on the very first load
    if (force) setPositionsRefreshing(true);
    try {
      const data = await apiGet(`/api/batch/app-init${force ? "?refresh=true" : ""}`);
      if (data) {
        if (data.positions?.success) {
          const open = data.positions.open_positions || [];
          const settled = data.positions.settled_bets || [];
          const p = data.positions.pnl || null;
          setOpenPositions(open); setSettledBets(settled); setPnl(p);
          _tCache.open = open; _tCache.settled = settled; _tCache.pnl = p;
        }
        if (data.wallet?.connected) { setBalance(data.wallet.balance || 0); _tCache.balance = data.wallet.balance || 0; }
        if (data.cashout_estimates) { setCashoutEstimates(data.cashout_estimates); _tCache.est = data.cashout_estimates; }
        _tCache.ready = true;
      }
    } finally { setLoading(false); if (force) setPositionsRefreshing(false); }
  }, []);

  const fetchHistory = useCallback(async (force) => {
    if (force) setHistoryRefreshing(true);
    try {
      const data = await apiGet(`/api/trade/history${force ? "?refresh=true" : ""}`);
      if (data) { const t = data.trades || []; setHistory(t); _tCache.history = t; }
    } finally { if (force) setHistoryRefreshing(false); }
  }, []);

  const checkSetup = useCallback(async () => {
    if (isAdmin) { setHasCloudbetKey(true); _tCache.cbKey = true; return; }
    const data = await apiGet("/api/user/profile");
    if (data) { const k = data.has_cloudbet_key || false; setHasCloudbetKey(k); _tCache.cbKey = k; }
  }, [isAdmin]);

  // Initial load (runs once). Cached data renders instantly; this refreshes it silently.
  useEffect(() => {
    fetchPositions(); fetchHistory(); checkSetup();
  }, [fetchPositions, fetchHistory, checkSetup]);

  // Re-read positions whenever the user switches to the Positions tab so Settled/Open reflect the
  // latest cache (e.g. after a History refresh or a background update) instead of a frozen snapshot.
  useEffect(() => {
    if (subTab === "positions") fetchPositions();
  }, [subTab, fetchPositions]);

  // Background refresh of positions every 30s (only while the Positions sub-tab is active).
  // Switching sub-tabs only resets the timer — it never triggers a full blocking refetch.
  useEffect(() => {
    const i = setInterval(() => { if (subTab === "positions") fetchPositions(); }, 30000);
    return () => clearInterval(i);
  }, [fetchPositions, subTab]);

  const doCashout = async () => {
    if (!cashoutTarget) return;
    setCashoutLoading(true);
    try {
      const res = await apiPost("/api/cloudbet/wallet/cashout", { reference_id: cashoutTarget.reference_id });
      if (res && res.success) { toast.success(res.message || "Cashed out!"); fetchPositions(true); }
      else toast.error((res && res.error) || "Cashout failed");
    } catch { toast.error("Failed to execute cashout"); }
    finally { setCashoutLoading(false); setCashoutTarget(null); }
  };

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold text-white mb-4">Trading</h1>
      <div className="flex gap-2 mb-4">
        {[{ k: "positions", l: "Positions", I: TrendingUp }, { k: "history", l: "History", I: History }].map(({ k, l, I }) => (
          <button key={k} onClick={() => setSubTab(k)} data-testid={`trading-tab-${k}`}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-[13px] font-semibold ${subTab === k ? "bg-[#10b981]/15 border border-[#10b981] text-[#10b981]" : "bg-[#1e293b] text-[#6b7280]"}`}>
            <I size={16} /> {l}
          </button>
        ))}
      </div>

      {subTab === "positions" && (
        <>
          {pnl && typeof pnl.net_pnl === "number" && (
            <div className="rounded-2xl p-5 mb-4 border-2 relative" style={{ backgroundColor: pnl.net_pnl >= 0 ? "#10b98110" : "#ef444410", borderColor: pnl.net_pnl >= 0 ? "#10b981" : "#ef4444" }}>
              <button onClick={() => setCalendarOpen(true)} data-testid="open-calendar" className="absolute top-3.5 right-3.5 w-9 h-9 rounded-full bg-[#1e293b] border border-[#334155] flex items-center justify-center text-[#9ca3af] hover:text-white"><CalendarDays size={18} /></button>
              <p className="text-sm text-[#9ca3af] font-semibold mb-3 text-center">Portfolio P&L</p>
              <div className="flex justify-around mb-4">
                <div className="text-center"><p className="text-3xl font-bold" style={{ color: pnl.net_pnl >= 0 ? "#10b981" : "#ef4444" }}>{pnl.net_pnl >= 0 ? "+" : ""}{pnl.net_pnl.toFixed(2)} USDT</p><p className="text-[12px] text-[#6b7280] mt-0.5">Net Profit</p></div>
                <div className="text-center"><p className="text-2xl font-bold" style={{ color: pnl.pnl_percent >= 0 ? "#10b981" : "#ef4444" }}>{pnl.pnl_percent >= 0 ? "+" : ""}{pnl.pnl_percent.toFixed(1)}%</p><p className="text-[12px] text-[#6b7280] mt-0.5">Return</p></div>
              </div>
              <div className="flex bg-[#0f172a]/50 rounded-xl p-3">
                <Stat label="Settled" value={pnl.total_staked.toFixed(0)} />
                <div className="w-px bg-[#334155]" />
                <Stat label="At Risk" value={(pnl.open_stake || 0).toFixed(0)} />
                <div className="w-px bg-[#334155]" />
                <Stat label="Wins" value={pnl.wins} color="#10b981" />
                <div className="w-px bg-[#334155]" />
                <Stat label="Losses" value={pnl.losses} color="#ef4444" />
                <div className="w-px bg-[#334155]" />
                <Stat label="Win Rate" value={`${pnl.win_rate.toFixed(0)}%`} color="#f59e0b" />
              </div>
            </div>
          )}

          {strategyBreakdown.length > 0 && (
            <div className="bg-[#1e293b] rounded-2xl p-4 mb-4 border border-[#334155]" data-testid="strategy-breakdown">
              <div className="flex items-center gap-1.5 mb-3">
                <Sparkles size={16} color="#a78bfa" />
                <span className="text-base font-bold text-[#e2e8f0] flex-1">Strategy Performance</span>
                <span className="text-[11px] text-[#6b7280]">{settledBets.length} settled bets</span>
              </div>
              <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-3 gap-y-0 text-[10px] text-[#6b7280] font-semibold uppercase tracking-wide pb-2 border-b border-[#334155]">
                <span>Strategy</span><span className="text-center">W-L</span><span className="text-center">Win %</span><span className="text-right">Net P&L</span>
              </div>
              {strategyBreakdown.map((s) => (
                <div key={s.name} data-testid={`strategy-row-${s.name}`} className="grid grid-cols-[1fr_auto_auto_auto] gap-x-3 items-center py-2 border-b border-[#334155]/40 last:border-0">
                  <div className="min-w-0">
                    <p className="text-[13px] font-semibold text-[#e2e8f0] truncate">{s.name}</p>
                    <p className="text-[10px] text-[#6b7280]">{s.roi >= 0 ? "+" : ""}{s.roi.toFixed(1)}% ROI · {s.staked.toFixed(0)} staked</p>
                  </div>
                  <span className="text-center text-[12px] font-semibold"><span className="text-[#10b981]">{s.wins}</span><span className="text-[#6b7280]">-</span><span className="text-[#ef4444]">{s.losses}</span></span>
                  <span className="text-center text-[12px] font-bold" style={{ color: s.winRate >= 50 ? "#10b981" : "#f59e0b" }}>{s.winRate.toFixed(0)}%</span>
                  <span className="text-right text-[13px] font-bold" style={{ color: s.net >= 0 ? "#10b981" : "#ef4444" }}>{s.net >= 0 ? "+" : ""}{s.net.toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}

          {hasCloudbetKey === false ? (
            <div className="bg-[#1e293b] border border-dashed border-[#3b82f6]/50 rounded-2xl p-4 mb-3" data-testid="connect-cloudbet-card">
              <button
                onClick={() => onNavigate && onNavigate("settings")}
                data-testid="connect-cloudbet-btn"
                className="w-full flex items-center gap-3 text-left hover:opacity-90"
              >
                <div className="w-12 h-12 shrink-0 rounded-full bg-[#3b82f6]/10 border border-[#3b82f6] flex items-center justify-center"><Cloud size={24} color="#3b82f6" /></div>
                <div className="flex-1">
                  <p className="text-white font-bold">Connect Cloudbet to start trading</p>
                  <p className="text-[13px] text-[#9ca3af]">Tap here to open Settings and paste your Cloudbet API key (Read + Trading permissions).</p>
                </div>
                <ChevronRight size={22} color="#3b82f6" className="shrink-0" />
              </button>
              <div className="flex flex-col sm:flex-row gap-2 mt-3 pt-3 border-t border-[#334155]">
                <button
                  onClick={() => onNavigate && onNavigate("settings")}
                  data-testid="connect-cloudbet-settings-btn"
                  className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#3b82f6] text-white text-[13px] font-bold px-3 py-2.5 rounded-lg"
                >
                  <Wallet size={15} /> Add API Key in Settings
                </button>
                <a
                  href={CLOUDBET_REFERRAL_URL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="cloudbet-referral-link"
                  className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#10b981]/10 border border-[#10b981]/40 text-[#10b981] text-[13px] font-bold px-3 py-2.5 rounded-lg hover:bg-[#10b981]/15"
                >
                  <Gift size={15} /> New? Create Cloudbet account <ExternalLink size={13} />
                </a>
              </div>
            </div>
          ) : hasCloudbetKey === true ? (
            <div className={`bg-[#1e293b] rounded-2xl p-4 mb-3 border ${balance > 0 ? "border-[#10b981] border-2" : "border-[#334155]"}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-11 h-11 rounded-full bg-[#10b981]/15 flex items-center justify-center"><Wallet size={22} color="#10b981" /></div>
                <div className="flex-1"><p className="text-lg font-bold text-white">Cloudbet Wallet</p><p className="text-[13px] text-[#9ca3af]">{balance > 0 ? "● Connected" : "○ Awaiting connection"}</p></div>
              </div>
              <div className="flex justify-between items-center bg-[#0f172a] rounded-xl px-4 py-3.5"><span className="text-sm text-[#9ca3af]">Available Balance</span><span className="text-xl font-bold text-[#10b981]">{balance > 0 ? `${balance.toFixed(2)} USDT` : "—"}</span></div>
            </div>
          ) : null}

          {/* ── PENDING: submitted, awaiting Cloudbet acceptance ── */}
          {pendingPositions.length > 0 && (
            <>
              <div className="flex items-center gap-1.5 mb-2.5" data-testid="pending-positions-header">
                <Clock size={18} color="#f59e0b" />
                <span className="text-base font-bold text-[#e2e8f0] flex-1">Pending ({pendingPositions.length})</span>
                <span className="text-[12px] text-[#f59e0b] font-semibold">awaiting confirmation</span>
              </div>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 mb-4">
                {pendingPositions.map((pos) => {
                  const market = parseMarket(pos.market_url, pos.event_name, pos.backed_player, pos.legs, pos.backed_players);
                  const MIcon = market.Icon;
                  return (
                    <div key={pos.reference_id} className="bg-[#1e293b] rounded-2xl p-4" style={{ borderLeft: "3px solid #f59e0b" }} data-testid="pending-position-card">
                      <div className="flex justify-between items-center mb-2">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-bold" style={{ backgroundColor: market.color + "20", borderColor: market.color + "40", color: market.color }}><MIcon size={12} /> {market.label}</span>
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#f59e0b]"><span className="w-1.5 h-1.5 rounded-full bg-[#f59e0b] bp-pulse" /> PENDING</span>
                      </div>
                      <div className="mb-2"><StrategyTag strategy={pos.strategy} testId="pending-position-strategy" /></div>
                      <p className="text-[15px] font-semibold text-white mb-2.5">{pos.event_name}</p>
                      <div className="flex gap-1">
                        {(!pos.legs || pos.legs <= 1) && <div className="flex-1 bg-[#0f172a] rounded-lg p-2 text-center"><p className="text-[10px] text-[#6b7280]">Selection</p><p className="text-[13px] font-bold text-[#f59e0b] truncate">{market.selection}</p></div>}
                        <div className="flex-1 bg-[#0f172a] rounded-lg p-2 text-center"><p className="text-[10px] text-[#6b7280]">Odds</p><p className="text-[13px] font-bold text-[#e2e8f0]">{pos.odds.toFixed(3)}x</p></div>
                        <div className="flex-1 bg-[#0f172a] rounded-lg p-2 text-center"><p className="text-[10px] text-[#6b7280]">Stake</p><p className="text-[13px] font-bold text-[#e2e8f0]">{pos.stake} USDT</p></div>
                      </div>
                      <p className="text-[12px] text-[#f59e0b] italic mt-3 pt-3 border-t border-[#334155]">Submitted to Cloudbet — awaiting acceptance. Becomes Active once confirmed.</p>
                      <p className="text-[11px] text-[#4b5563] mt-2">Placed: {fmtTime(pos.placed_at)}</p>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          <div className="flex items-center gap-1.5 mb-2.5"><Lock size={18} color="#10b981" /><span className="text-base font-bold text-[#e2e8f0] flex-1">Active Positions ({activePositions.length})</span>{activePositions.length > 0 && <span className="text-[12px] text-[#f59e0b] font-semibold">{atRiskTotal.toFixed(0)} USDT at risk</span>}</div>

          {loading && openPositions.length === 0 ? (
            <LoadingBlock message="Loading positions from Cloudbet…" />
          ) : activePositions.length === 0 ? (
            <div className="flex flex-col items-center py-12" data-testid="no-active-positions"><Inbox size={48} color="#374151" /><p className="text-[#6b7280] mt-3">No active positions</p><p className="text-[#4b5563] text-[13px] mt-1">Bets appear here once confirmed by Cloudbet</p></div>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            {activePositions.map((pos) => {
            const est = cashoutEstimates[pos.reference_id];
            const market = parseMarket(pos.market_url, pos.event_name, pos.backed_player, pos.legs, pos.backed_players);
            const MIcon = market.Icon;
            return (
              <div key={pos.reference_id} className="bg-[#1e293b] rounded-2xl p-4 mb-2.5" style={{ borderLeft: "3px solid #10b981" }} data-testid="open-position-card">
                <div className="flex justify-between items-center mb-2">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-bold" style={{ backgroundColor: market.color + "20", borderColor: market.color + "40", color: market.color }}><MIcon size={12} /> {market.label}</span>
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#10b981]"><span className="w-1.5 h-1.5 rounded-full bg-[#10b981] bp-pulse" /> OPEN</span>
                </div>
                {pos.legs && pos.legs > 1 ? null : <div className="mb-2"><StrategyTag strategy={pos.strategy} testId="open-position-strategy" /></div>}
                {pos.legs > 1 && pos.backed_players?.length > 0 ? (
                  <div className="mb-2">{pos.backed_players.map((pl, idx) => (<div key={`${pos.reference_id}-leg-${idx}`} className="flex items-center mt-1"><span className="text-[#10b981] text-[13px] font-semibold">Leg {idx + 1}:</span><span className="text-[#e5e7eb] text-[13px] ml-1.5 truncate">{pl}</span></div>))}</div>
                ) : (<p className="text-[15px] font-semibold text-white mb-2.5">{pos.event_name}</p>)}
                <div className="flex gap-1">
                  {(!pos.legs || pos.legs <= 1) && <div className="flex-1 bg-[#0f172a] rounded-lg p-2 text-center"><p className="text-[10px] text-[#6b7280]">Selection</p><p className="text-[13px] font-bold text-[#10b981] truncate">{market.selection}</p></div>}
                  <div className="flex-1 bg-[#0f172a] rounded-lg p-2 text-center"><p className="text-[10px] text-[#6b7280]">Odds</p><p className="text-[13px] font-bold text-[#e2e8f0]">{pos.odds.toFixed(3)}x</p></div>
                  <div className="flex-1 bg-[#0f172a] rounded-lg p-2 text-center"><p className="text-[10px] text-[#6b7280]">Stake</p><p className="text-[13px] font-bold text-[#e2e8f0]">{pos.stake} USDT</p></div>
                  <div className="flex-1 bg-[#0f172a] rounded-lg p-2 text-center"><p className="text-[10px] text-[#6b7280]">Potential</p><p className="text-[13px] font-bold text-[#10b981]">+{pos.potential_profit} USDT</p></div>
                </div>
                {est?.available ? (
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#334155]">
                    <div>
                      <p className="text-[11px] text-[#6b7280]">Cash Out Value</p>
                      <p className="text-lg font-bold" style={{ color: est.net_profit >= 0 ? "#10b981" : "#ef4444" }}>
                        {est.cashout_value.toFixed(2)} USDT
                        <span className="text-[12px]"> ({est.net_profit >= 0 ? "+" : ""}{est.net_profit.toFixed(2)})</span>
                        {pos.stake > 0 && (
                          <span className="text-[12px] ml-1.5 px-1.5 py-0.5 rounded" data-testid="cashout-profit-pct"
                            style={{ backgroundColor: (est.net_profit >= 0 ? "#10b981" : "#ef4444") + "22" }}>
                            {est.net_profit >= 0 ? "+" : ""}{(est.net_profit / pos.stake * 100).toFixed(1)}%
                          </span>
                        )}
                      </p>
                    </div>
                    <button onClick={() => setCashoutTarget({ ...pos, est })} data-testid="cashout-btn" className="flex items-center gap-1.5 bg-[#10b981] hover:bg-[#059669] transition-colors text-white font-bold text-sm px-4 py-2.5 rounded-xl">Cash Out</button>
                  </div>
                ) : est ? (<p className="text-[12px] text-[#6b7280] italic mt-3 pt-3 border-t border-[#334155]">Cashout unavailable: {est.reason || "No live odds"}</p>) : null}
                <CashedOutLine pos={pos} testId="open-position-cashedout" />
                <p className="text-[11px] text-[#4b5563] mt-2">Placed: {fmtTime(pos.placed_at)}</p>
              </div>
            );
          })}
            </div>
          )}

          {settledBets.length > 0 && (
            <>
              <div className="flex items-center justify-between mb-2.5 mt-4">
                <div className="flex items-center gap-1.5"><CheckCircle2 size={18} color="#6b7280" /><span className="text-base font-bold text-[#e2e8f0]">Settled ({settledBets.length})</span></div>
                <button
                  onClick={() => fetchPositions(true)}
                  disabled={positionsRefreshing}
                  data-testid="positions-refresh-btn"
                  className="flex items-center gap-2 text-sm text-[#9ca3af] hover:text-white bg-[#1e293b] border border-[#334155] px-3 py-2 rounded-lg disabled:opacity-60"
                >
                  <RefreshCw size={16} className={positionsRefreshing ? "animate-spin" : ""} />
                  {positionsRefreshing ? "Syncing…" : "Refresh"}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-2.5">
              {sortedSettledBets.map((bet) => {
                const market = parseMarket(bet.market_url, bet.event_name, bet.backed_player, undefined, bet.backed_players);
                const MIcon = market.Icon;
                return (
                  <div key={bet.reference_id} className="bg-[#1e293b] rounded-xl p-3.5 mb-2" style={{ borderLeft: `3px solid ${statusColor(bet.status)}` }}>
                    <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ backgroundColor: market.color + "20", color: market.color }}><MIcon size={10} /> {market.label}</span>
                      <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold text-white" style={{ backgroundColor: statusColor(bet.status) + "30" }}>{bet.status}</span>
                      {(!bet.legs || bet.legs <= 1) && <StrategyTag strategy={bet.strategy} testId="settled-bet-strategy" />}
                    </div>
                    <p className="text-sm text-[#e2e8f0] font-semibold">{bet.event_name || bet.backed_player || "—"}</p>
                    <p className="text-[12px] text-[#10b981]">{market.selection}</p>
                    <div className="flex gap-3 mt-1"><span className="text-[12px] text-[#9ca3af]">Odds: {bet.odds.toFixed(3)}x</span><span className="text-[12px] text-[#9ca3af]">Stake: {bet.stake} USDT</span><span className="text-[12px] font-bold" style={{ color: statusColor(bet.status) }}>{isLossStatus(bet.status) || bet.profit < 0 ? "" : "+"}{bet.profit.toFixed(2)} USDT</span></div>
                    <CashedOutLine pos={bet} testId="settled-bet-cashedout" />
                    <p className="text-[11px] text-[#4b5563] mt-1">{fmtTime(bet.placed_at)}{bet.settled_at ? ` → ${fmtTime(bet.settled_at)}` : ""}</p>
                  </div>
                );
              })}
              </div>
            </>
          )}
        </>
      )}

      {subTab === "history" && (
        <div>
          <div className="flex items-center justify-between mb-2.5">
            <div className="flex items-center gap-1.5"><Cloud size={18} color="#10b981" /><span className="text-base font-bold text-white">Cloudbet Trade History ({history.length})</span></div>
            <button
              onClick={() => fetchHistory(true)}
              disabled={historyRefreshing}
              data-testid="history-refresh-btn"
              className="flex items-center gap-2 text-sm text-[#9ca3af] hover:text-white bg-[#1e293b] border border-[#334155] px-3 py-2 rounded-lg disabled:opacity-60"
            >
              <RefreshCw size={16} className={historyRefreshing ? "animate-spin" : ""} />
              {historyRefreshing ? "Syncing…" : "Refresh"}
            </button>
          </div>
          {history.length === 0 ? (
            <div className="flex flex-col items-center py-12"><History size={56} color="#374151" /><p className="text-[#6b7280] mt-3">No trades yet</p><p className="text-[#4b5563] text-[13px] mt-1">Trades will appear here when strategies execute</p></div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-2">
            {history.map((trade, i) => {
            const market = parseMarket(trade.market_url, trade.event_name, trade.player);
            const color = statusColor(trade.status);
            const name = trade.event_name && trade.event_name !== "Unknown" ? trade.event_name : (market.selection || trade.player || "Unknown");
            // Show the backed player (green) when it's a real selection distinct from the matchup line.
            const sel = market.selection;
            const showSel = sel && sel !== "Unknown" && sel !== name && (market.label === "Backing" || market.label === "MULTI" || market.label.startsWith("MULTI") || market.label === "Exact Sets");
            return (
              <div key={trade.id || trade.reference_id || `${trade.event_name || "trade"}-${i}`} className="bg-[#1e293b] rounded-xl p-3.5 mb-2" style={{ borderLeft: `3px solid ${color}` }}>
                <div className="flex justify-between items-center mb-1.5"><span className="text-sm text-white font-semibold truncate mr-2">{name}</span><span className="px-2 py-0.5 rounded text-[10px] font-bold" style={{ backgroundColor: color + "30", color }}>{trade.status}</span></div>
                {showSel && <p className="text-[12px] text-[#10b981] mb-1.5" data-testid="history-backed-player"><span className="text-[#6b7280]">Backing: </span>{sel}</p>}
                {(!trade.legs || trade.legs <= 1) && <div className="mb-1.5"><StrategyTag strategy={trade.strategy} testId="history-strategy" /></div>}
                <div className="flex gap-3 items-center"><span className="text-[12px] text-[#9ca3af]">Stake: {parseFloat(trade.stake || 0).toFixed(2)} {trade.currency || "USDT"}</span><span className="text-[12px] text-[#9ca3af]">Odds: {parseFloat(trade.odds || 0).toFixed(3)}x</span>{trade.profit !== undefined && trade.profit !== 0 && <span className="text-[12px] font-bold" style={{ color: trade.profit >= 0 ? "#10b981" : "#ef4444" }}>{trade.profit >= 0 ? "+" : ""}{trade.profit.toFixed(2)}</span>}</div>
                <CashedOutLine pos={trade} testId="history-cashedout" />
                <p className="text-[11px] text-[#6b7280] mt-1">{trade.executed_at ? new Date(trade.executed_at.replace("Z", "+00:00")).toLocaleString() : ""}</p>
              </div>
            );
          })}
            </div>
          )}
        </div>
      )}

      <Modal open={calendarOpen} onClose={() => setCalendarOpen(false)} title={undefined} maxWidth="max-w-2xl" testId="calendar-modal">
        <CalendarView onClose={() => setCalendarOpen(false)} />
      </Modal>

      <ConfirmDialog
        open={!!cashoutTarget} onClose={() => setCashoutTarget(null)} onConfirm={doCashout} loading={cashoutLoading}
        title="Cash Out Position" confirmText="Cash Out"
        message={cashoutTarget ? `Cashout value: ${cashoutTarget.est.cashout_value.toFixed(2)} USDT\nNet P/L: ${cashoutTarget.est.net_profit >= 0 ? "+" : ""}${cashoutTarget.est.net_profit.toFixed(2)} USDT\n\nThis places a hedge bet to lock in your return.` : ""}
      />
    </div>
  );
}
